import React, { useMemo, useReducer, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Box, Container, makeStyles } from '@material-ui/core';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import { ModalContainer, ModalFooter, ModalHeader } from 'components/@common/Modal';
import {
  ProductCardValidationProvider,
  useProductCardValidation
} from 'components/@common/ProductLicenses/product-card-validation-provider';
import StudentProductLicenseCard from 'components/@common/ProductLicenses/StudentProductLicenseCard';
import UnsavedChangesWarning from 'components/@common/ProductLicenses/UnsavedChangesWarning';
import ModalCloseButton from 'components/@common/SlideModal/ModalCloseButton';
import PageContainer from 'components/@layout/PageContainer';
import Config from 'config';
import BULK_GROUP_ENROLLMENT_ACTION from 'graph/classroom/bulk-group-enrollment-mutation';
import GET_CLASSROOM_QUERY from 'graph/classroom/get-classroom-query';
import GET_CLASSROOM_LICENSES_STUDENTS from 'graph/composite/get-classroom-licenses-students-query';
import BULK_ASSIGN_AVAILABLE_LICENSES from 'graph/license/bulk-assign-available-licenses-mutation';
import GET_ORGANIZATION_AVAILABLE_LICENSES from 'graph/organization/get-organization-available-licenses';
import GET_STUDENT from 'graph/student/get-student-query';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import { filter, flow, map, uniq, sortBy } from 'lodash/fp';
import useStore from 'store';
import { AlertVariant } from 'types/enums';
import {
  ProductType,
  OrganizationLicenseUsage,
  StudentLicenseUsage,
  LicenseAction,
  BulkLicenseActionInput
} from 'types/schema-generated-types';
import { getFullName } from 'utilities/name-utils';
import useDefaultGroupSettings from '../../../hooks/use-default-group-settings';
import licenseUpdateReducer, { ActionType as UpdateLicenseActionType } from './reducer';

interface ManageStudentProductsModalProps {
  onClose: () => void;
}

const { privilegesGroups } = Config;

const ManageStudentProductsModal = ({ onClose }: ManageStudentProductsModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ManageStudentProducts');
  const { classroomId, studentId } = useParseActionRouteParams();
  const [{ userPrivilegesCache }, { openAlert }] = useStore();
  const { getGroupEnrollments, getGroupUnenrollments } = useDefaultGroupSettings();
  const [{ invalidProductFamilyIds }] = useProductCardValidation();
  const [highlightProductFamilyId, setHighlightProductFamilyId] = useState<number | null>(null);
  const [licenseUpdates, dispatch] = useReducer(licenseUpdateReducer, []);

  const [
    loadClassroom,
    { data: classroomData, loading: classroomLoading, error: classroomError }
  ] = useLazyQuery(GET_CLASSROOM_QUERY);

  const [
    loadStudent,
    { data: studentData, loading: studentLoading, error: studentError }
  ] = useLazyQuery(GET_STUDENT);

  const [loadLicenseUsage, { data, loading: licensesLoading, error: licensesError }] = useLazyQuery(
    GET_ORGANIZATION_AVAILABLE_LICENSES
  );

  const [
    bulkAssignAvailableLicenses,
    { called: assignCalled, loading: assignLoading }
  ] = useMutation(BULK_ASSIGN_AVAILABLE_LICENSES);

  const [
    bulkGroupEnrollmentAction,
    { called: groupEnrollmentCalled, loading: groupEnrollmentLoading }
  ] = useMutation(BULK_GROUP_ENROLLMENT_ACTION);

  useEffect(() => {
    if (+classroomId) loadClassroom({ variables: { id: +classroomId } });
  }, [classroomId, loadClassroom]);

  useEffect(() => {
    if (+studentId) loadStudent({ variables: { where: { id: +studentId } } });
  }, [studentId, loadStudent]);

  useEffect(() => {
    if (classroomData?.classroom) {
      loadLicenseUsage({
        variables: { organizationId: classroomData.classroom.organizationId }
      });
    }
  }, [classroomData, loadLicenseUsage]);

  const hasLicenseAssignPrivileges = userPrivilegesCache.isPrivileged(
    [...privilegesGroups.assignClassroomLicense, ...privilegesGroups.assignOrgLicense],
    {
      classroomId: classroomData?.classroom?.id,
      organizationId: classroomData?.classroom?.organizationId
    }
  );

  const assignedProductFamilyCurriculumMap = useMemo(() => {
    const studentLicenseUsage: StudentLicenseUsage[] = studentData?.student?.licenseUsage ?? [];

    const result = new Map<number, { id: number; manualAssignment: boolean }[]>();
    studentLicenseUsage
      .filter(lu => lu.assignedCount > 0 && !!lu.productCurriculum)
      .forEach(x => {
        if (!x.productCurriculum) return;
        const existing = result.get(x.product.family.id) ?? [];
        result.set(x.product.family.id, [
          ...existing,
          { id: x.productCurriculum.id, manualAssignment: x.manualAssignment }
        ]);
      });

    return result;
  }, [studentData]);

  const organizationLicenseUsage: OrganizationLicenseUsage[] =
    data?.organization?.rootOrganization?.licenseUsage ?? [];

  const productFamilyIds = useMemo(
    () =>
      flow(
        filter((lu: OrganizationLicenseUsage) => lu.product.type === ProductType.STUDENT),
        sortBy('product.family.name'),
        map('product.family.id'),
        uniq
      )(organizationLicenseUsage),
    [organizationLicenseUsage]
  );

  const handleLicenseAssign = (productFamilyId: number, productCurriculumId: number | number[]) =>
    dispatch({
      type: UpdateLicenseActionType.ADD_UPDATES,
      payload: {
        action: LicenseAction.ASSIGN,
        productFamilyId,
        productCurriculumIds: ([] as number[]).concat(productCurriculumId)
      }
    });

  const handleLicenseUnassign = (
    productFamilyId: number,
    productCurriculumId: number | number[]
  ) => {
    dispatch({
      type: UpdateLicenseActionType.ADD_UPDATES,
      payload: {
        action: LicenseAction.UNASSIGN,
        productFamilyId,
        productCurriculumIds: ([] as number[]).concat(productCurriculumId)
      }
    });
  };

  const handleSubmit = useCallback(async () => {
    if (invalidProductFamilyIds.length > 0) {
      const productFamilyId = productFamilyIds.find(id => invalidProductFamilyIds.includes(id));
      if (productFamilyId) {
        setHighlightProductFamilyId(productFamilyId);
        setTimeout(() => setHighlightProductFamilyId(null), 3000);
      }

      return;
    }

    const assignRequests: BulkLicenseActionInput[] = licenseUpdates.map(u => ({
      studentId: +studentId,
      action: u.action,
      productCurriculum: { id: u.productCurriculumId },
      replaceExistingProductFamilyLicense: true,
      skipAlreadyLicensed: true
    }));

    await bulkAssignAvailableLicenses({
      variables: { where: { organizationId: data?.organization?.id }, assignRequests },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_ORGANIZATION_AVAILABLE_LICENSES,
          variables: { organizationId: classroomData?.classroom?.organizationId }
        },
        { query: GET_CLASSROOM_QUERY, variables: { id: +classroomId } },
        { query: GET_STUDENT, variables: { where: { id: +studentId } } }
      ]
    });

    const groupEnrollmentUpdates = [
      ...getGroupEnrollments(+classroomId, [studentData?.student], assignRequests),
      ...getGroupUnenrollments(+classroomId, [studentData?.student], assignRequests)
    ];

    if (groupEnrollmentUpdates.length > 0) {
      await bulkGroupEnrollmentAction({
        variables: { data: groupEnrollmentUpdates },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GET_CLASSROOM_LICENSES_STUDENTS,
            variables: { classroomId: +classroomId }
          }
        ]
      });
    }

    openAlert({
      variant: AlertVariant.SUCCESS,
      title: t('Student Licenses Updated'),
      text: t(`The student licenses have been successfully updated.`)
    });
  }, [
    bulkAssignAvailableLicenses,
    bulkGroupEnrollmentAction,
    classroomId,
    classroomData,
    data,
    getGroupEnrollments,
    getGroupUnenrollments,
    invalidProductFamilyIds,
    licenseUpdates,
    productFamilyIds,
    studentData,
    studentId,
    openAlert,
    t
  ]);

  useEffect(() => {
    const licenseAssignmentComplete = assignCalled && !assignLoading;
    const groupEnrollmentComplete =
      !groupEnrollmentCalled || (groupEnrollmentCalled && !groupEnrollmentLoading);
    if (licenseAssignmentComplete && groupEnrollmentComplete) onClose();
  }, [assignCalled, assignLoading, groupEnrollmentCalled, groupEnrollmentLoading, onClose]);

  const handleCancelClick = () => {
    if (licenseUpdates.length > 0) {
      openAlert({
        cancelButtonText: t('Exit without saving'),
        confirmButtonText: t('Go back'),
        onCancel: () => onClose(),
        onSubmit: () => false,
        reverseButtons: false,
        text: t('You have unsaved changes. Do you want to save them?'),
        variant: AlertVariant.WARNING
      });

      return;
    }

    onClose();
  };

  return (
    <PageContainer
      loading={classroomLoading || studentLoading || licensesLoading || assignLoading}
      error={classroomError ?? studentError ?? licensesError}
    >
      <ModalContainer
        className={classes.body}
        Header={
          <>
            <ModalCloseButton onClick={handleCancelClick} tabIndex={0} />
            <ModalHeader
              title={t('manageStudentProducts', {
                studentName: getFullName(studentData?.student ?? {})
              })}
            />
          </>
        }
        Footer={
          <ModalFooter
            onCancel={handleCancelClick}
            onSubmit={productFamilyIds.length > 0 ? handleSubmit : undefined}
            submitDisabled={assignLoading}
          >
            <Box marginLeft={4}>{licenseUpdates.length > 0 && <UnsavedChangesWarning />}</Box>
          </ModalFooter>
        }
      >
        {productFamilyIds.length === 0 && (
          <EmptyResultSetMessage
            style={{ marginTop: '12px' }}
            message={t('No available licenses', { ns: 'EmptyResultSetMessage' })}
            subtitle={t(`There are no licenses available to assign to students.`, {
              ns: 'EmptyResultSetMessage'
            })}
          />
        )}
        {productFamilyIds.map(productFamilyId => {
          const assignedProductCurriculums = assignedProductFamilyCurriculumMap.get(
            productFamilyId
          );

          const assignedProductCurriculumIds = assignedProductCurriculums?.map(x => x.id);

          return (
            <Container key={productFamilyId} maxWidth="lg" className={classes.cardContainer}>
              <StudentProductLicenseCard
                productFamilyId={productFamilyId}
                assignable={hasLicenseAssignPrivileges}
                assignedProductCurriculumId={assignedProductCurriculumIds?.[0]}
                gradeLevel={studentData?.student?.gradeLevel}
                pendingUpdates={licenseUpdates.filter(x => x.productFamilyId === productFamilyId)}
                organizationLicenseUsage={organizationLicenseUsage as OrganizationLicenseUsage[]}
                error={highlightProductFamilyId === productFamilyId}
                onLicenseAssign={handleLicenseAssign}
                onLicenseUnassign={handleLicenseUnassign}
              />
            </Container>
          );
        })}
      </ModalContainer>
    </PageContainer>
  );
};

const ManageStudentProductsModalWrapper = (props: ManageStudentProductsModalProps) => (
  <ProductCardValidationProvider>
    <ManageStudentProductsModal {...props} />
  </ProductCardValidationProvider>
);

const useStyles = makeStyles(theme => ({
  body: {
    background: '#EEEEEE'
  },
  cardContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  nextButton: {
    marginRight: theme.spacing(6)
  },
  cancelButton: {
    marginRight: theme.spacing(3)
  },
  buttonLabels: {
    fontSize: '1rem',
    textTransform: 'initial'
  },
  saveChangesMessage: {
    marginLeft: theme.spacing(6)
  }
}));

export default ManageStudentProductsModalWrapper;
